import { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useApp, EQUIPMENT_CATEGORIES, SERVICE_CATEGORIES } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import CityAutocomplete from "@/components/CityAutocomplete";
import NeighborhoodAutocomplete from "@/components/NeighborhoodAutocomplete";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import TurnstileWidget from "@/components/TurnstileWidget";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/lib/supabase";
import { geocodeCity } from "@/lib/geo";
import { track } from "@/lib/analytics";
import { ImagePlus, X, MapPin, Tractor, Wrench, Phone, MessageCircle, Mail } from "lucide-react";

const DAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sab", "Dom"];
const STATES = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];


function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}
export default function CreateListing() {
  useEffect(() => { track("criar_anuncio_abriu", { type: "funnel" }); }, []);
  const { user, addListing } = useApp();
  const navigate = useNavigate();

  const [listingType, setListingType] = useState<"equipamento" | "servico">("equipamento");
  const [form, setForm] = useState({
    title: "", category: "", description: "", price: "", priceUnit: "por hora",
    phone: "", whatsapp: "", email: "",
    city: user?.city || "", state: user?.state || "", neighborhood: "",
    availability: ["Seg", "Ter", "Qua", "Qui", "Sex"] as string[],
  });
  const [images, setImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [aceito, setAceito] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [plan, setPlan] = useState<any>(null);
  useEffect(() => {
    if (!user) return;
    supabase.rpc("my_plan_summary").then(({ data }) => setPlan(data || null));
  }, [user?.id]);

  if (!user) return <Navigate to="/login" />;
  if (user.blocked) return (
    <div className="container mx-auto px-4 py-20 text-center">
      <div className="max-w-md mx-auto bg-destructive/10 border border-destructive/20 rounded-lg p-8">
        <h2 className="text-xl font-heading font-bold text-destructive mb-2">Conta bloqueada</h2>
        <p className="text-muted-foreground">Sua conta foi bloqueada e não pode criar novos anúncios. Entre em contato com o suporte: agroaluga@outlook.com</p>
      </div>
    </div>
  );

  const categories = listingType === "equipamento" ? EQUIPMENT_CATEGORIES : SERVICE_CATEGORIES;

  const set = (k: string, v: string) => setForm((p) => ({ ...p, [k]: v }));
  const toggleDay = (d: string) => setForm((p) => ({
    ...p,
    availability: p.availability.includes(d)
      ? p.availability.filter((x) => x !== d)
      : [...p.availability, d],
  }));

  const handleTypeChange = (type: "equipamento" | "servico") => {
    setListingType(type);
    setForm((p) => ({ ...p, category: "" })); // resetar categoria ao trocar tipo
  };

  const compressImage = (file: File, maxWidthPx = 1200, qualityJpeg = 0.82): Promise<Blob> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const scale = Math.min(1, maxWidthPx / img.width);
        const canvas = document.createElement("canvas");
        canvas.width  = Math.round(img.width  * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((b) => b ? resolve(b) : reject(new Error("Canvas vazio")), "image/jpeg", qualityJpeg);
      };
      img.onerror = reject;
      img.src = url;
    });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    track("criar_anuncio_foto", { type: "funnel" });
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Limite de 5 fotos por anúncio
    const remaining = 5 - images.length;
    if (remaining <= 0) {
      alert("Limite de 5 fotos por anúncio atingido.");
      return;
    }
    const selected = Array.from(files).slice(0, remaining);

    // Tipos permitidos
    const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    const MAX_SIZE_MB = 10;

    const invalid = selected.filter(f => !ALLOWED_TYPES.includes(f.type));
    if (invalid.length > 0) {
      alert("Apenas imagens JPG, PNG ou WebP são permitidas.");
      return;
    }
    const tooBig = selected.filter(f => f.size > MAX_SIZE_MB * 1024 * 1024);
    if (tooBig.length > 0) {
      alert(`Imagens muito grandes. Máximo permitido: ${MAX_SIZE_MB}MB por foto.`);
      return;
    }

    setUploading(true);
    const uploaded: string[] = [];

    for (const file of selected) {
      try {
        // Comprime para max 1200px e ~82% qualidade JPEG
        const compressed = await compressImage(file);
        const path = `${user.id}/${Date.now()}.jpg`;
        const { error } = await supabase.storage
          .from("listings")
          .upload(path, compressed, { contentType: "image/jpeg", upsert: false });
        if (!error) {
          const { data } = supabase.storage.from("listings").getPublicUrl(path);
          uploaded.push(data.publicUrl);
        }
      } catch (err) {
        console.error("Erro ao processar imagem:", err);
      }
    }

    setImages((prev) => [...prev, ...uploaded]);
    setUploading(false);
  };

  const removeImage = (url: string) => setImages((prev) => prev.filter((i) => i !== url));

  const handlePublish = async () => {
    if (!form.title || form.title.length < 10 || !form.category || !form.description || form.description.length < 50 || !form.price || !form.city || !form.state) {
      setError("Preencha todos os campos obrigatórios."); return;
    }
    if (!form.availability || form.availability.length === 0) {
      setError("Selecione ao menos um dia de disponibilidade."); return;
    }
    if (!aceito) {
      setError("Você precisa aceitar os termos de uso para publicar."); return;
    }
    if (!turnstileToken) {
      setError("Confirme que você não é um robô (aguarde a verificação)."); return;
    }
    setError("");
    setLoading(true);
    try {
      // Valida o captcha no servidor antes de criar o anúncio
      const { data: verify } = await supabase.functions.invoke("verify-turnstile", {
        body: { token: turnstileToken },
      });
      if (!verify?.success) {
        setError("Falha na verificação de segurança. Recarregue a página e tente novamente.");
        setLoading(false);
        return;
      }
      let coords = null;
      if (form.neighborhood) {
        try {
          const q = encodeURIComponent(`${form.neighborhood}, ${form.city}, ${form.state}, Brasil`);
          const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1&countrycodes=br`, { headers: { "Accept-Language": "pt-BR" } });
          const data = await res.json();
          if (data[0]) coords = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
        } catch {}
      }
      if (!coords) coords = await geocodeCity(form.city, form.state);
      const finalImages = images.length > 0
        ? images
        : ["https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=600&h=400&fit=crop"];

      await addListing({
        title: form.title,
        category: form.category,
        description: form.description,
        price: parseFloat(form.price),
        priceUnit: form.priceUnit,
        city: form.city,
        neighborhood: form.neighborhood,
        state: form.state,
        latitude: coords?.lat,
        longitude: coords?.lng,
        images: finalImages,
        availability: form.availability,
        phone: form.phone,
        whatsapp: form.whatsapp,
        email: form.email,
        ownerId: user.id,
        ownerName: user.name,
        featured: false,
        status: "pending",
        listingType,
      } as any);
      track("criar_anuncio_publicou", { type: "funnel", props: { categoria: form.category, tipo: listingType } });
      navigate("/dashboard");
    } catch (e) {
      setError("Erro ao publicar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <h1 className="text-2xl font-heading font-bold mb-2">Criar Anúncio</h1>
      <p className="text-sm text-muted-foreground mb-6">
        {plan?.is_admin ? "Você é administrador — anúncios ilimitados." :
         plan?.listings_limit === -1 ? `Plano ${plan?.plan_name} — anúncios ilimitados.` :
         plan ? `Plano ${plan.plan_name} — ${plan.listings_left} de ${plan.listings_limit} anúncios restantes este mês.` :
         "Publique seu anúncio na AgroAluga."}
      </p>

      <div className="bg-card border border-border rounded-lg p-6 space-y-5">
        {error && <p className="text-sm text-destructive">{error}</p>}

        {/* Tipo de anúncio */}
        <div>
          <label className="text-sm font-medium mb-2 block">Tipo de anúncio *</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => handleTypeChange("equipamento")}
              className={`p-4 rounded-lg border text-left transition-all flex items-center gap-3 ${listingType === "equipamento" ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}
            >
              <Tractor className={`h-6 w-6 ${listingType === "equipamento" ? "text-primary" : "text-muted-foreground"}`} />
              <div>
                <p className={`font-semibold text-sm ${listingType === "equipamento" ? "text-primary" : ""}`}>Equipamento</p>
                <p className="text-xs text-muted-foreground">Máquinas e ferramentas</p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => handleTypeChange("servico")}
              className={`p-4 rounded-lg border text-left transition-all flex items-center gap-3 ${listingType === "servico" ? "border-accent bg-accent/10" : "border-border hover:border-accent/50"}`}
            >
              <Wrench className={`h-6 w-6 ${listingType === "servico" ? "text-accent" : "text-muted-foreground"}`} />
              <div>
                <p className={`font-semibold text-sm ${listingType === "servico" ? "text-accent-foreground" : ""}`}>Serviço</p>
                <p className="text-xs text-muted-foreground">Mao de obra e consultoria</p>
              </div>
            </button>
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-1">
            <label className="text-sm font-medium">Titulo *</label>
            <span className={`text-xs ${form.title.length < 10 ? "text-destructive" : "text-muted-foreground"}`}>{form.title.length}/10 min</span>
          </div>
          <Input value={form.title} onChange={(e) => set("title", e.target.value)}
            placeholder={listingType === "equipamento" ? "Ex: Trator New Holland — Indaial, SC" : "Ex: Mapeamento Aereo com Drone — Indaial, SC"}
            className={form.title.length > 0 && form.title.length < 10 ? "border-destructive" : ""} />
        </div>

        <div>
          <label className="text-sm font-medium mb-1 block">Categoria *</label>
          <Select value={form.category} onValueChange={(v) => set("category", v)}>
            <SelectTrigger><SelectValue placeholder="Selecione a categoria" /></SelectTrigger>
            <SelectContent>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <div className="flex justify-between items-center mb-1">
            <label className="text-sm font-medium">Descrição *</label>
            <span className={`text-xs ${form.description.length < 50 ? "text-destructive" : "text-muted-foreground"}`}>{form.description.length}/50 min</span>
          </div>
          <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={4}
            placeholder={listingType === "equipamento" ? "Descreva o equipamento, estado de conservação, marca, modelo..." : "Descreva o serviço, sua experiência, área de atuação..."}
            className={form.description.length > 0 && form.description.length < 50 ? "border-destructive" : ""} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium mb-1 block">Preço *</label>
            <Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="150" />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Unidade</label>
            <Select value={form.priceUnit} onValueChange={(v) => set("priceUnit", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["por hora", "por hectare", "por km", "por diaria", "por tarefa", "a combinar"].map((u) => (
                  <SelectItem key={u} value={u}>{u}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium mb-1 block flex items-center gap-1">
            <MapPin className="h-3 w-3" /> Localização *
          </label>
          <CityAutocomplete
            value={form.city}
            onChange={(city, state) => { set("city", city); set("state", state); set("neighborhood", ""); }}
          />
          <div className="mt-2">
            <NeighborhoodAutocomplete
              city={form.city}
              state={form.state}
              value={form.neighborhood}
              onChange={(neighborhood, lat, lng) => {
                set("neighborhood", neighborhood);
                if (lat && lng) { set("latitude", lat); set("longitude", lng); }
              }}
            />
          </div>
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Fotos</label>
          <div className="flex flex-wrap gap-3">
            {images.map((url) => (
              <div key={url} className="relative h-24 w-24 rounded-lg overflow-hidden border border-border">
                <img src={url} alt="preview" className="h-full w-full object-cover" />
                <button type="button" onClick={() => removeImage(url)}
                  className="absolute top-1 right-1 h-5 w-5 bg-destructive rounded-full flex items-center justify-center">
                  <X className="h-3 w-3 text-white" />
                </button>
              </div>
            ))}
            {images.length < 5 && (
              <label className="h-24 w-24 rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors">
                <ImagePlus className="h-6 w-6 text-muted-foreground" />
                <span className="text-xs text-muted-foreground mt-1">{uploading ? "Enviando..." : "Adicionar"}</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleImageUpload} disabled={uploading} />
              </label>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">Ate 5 fotos.</p>
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Disponibilidade</label>
          <div className="flex gap-2 flex-wrap">
            {DAYS.map((d) => (
              <button key={d} type="button" onClick={() => toggleDay(d)}
                className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${form.availability.includes(d) ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-primary/10"}`}>
                {d}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold uppercase tracking-wide text-primary">Contato</h3>
            <span className="text-xs font-normal text-muted-foreground">· opcional, como vão te encontrar</span>
          </div>
          <div className="flex items-center gap-2 bg-muted rounded-md px-3">
            <Phone className="h-4 w-4 text-primary shrink-0" />
            <Input placeholder="Telefone" value={form.phone} onChange={(e) => set("phone", formatPhone(e.target.value))} className="border-0 bg-transparent px-0 focus-visible:ring-0" />
          </div>
          <div className="flex items-center gap-2 bg-muted rounded-md px-3">
            <MessageCircle className="h-4 w-4 text-primary shrink-0" />
            <Input placeholder="WhatsApp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className="border-0 bg-transparent px-0 focus-visible:ring-0" />
          </div>
          <div className="flex items-center gap-2 bg-muted rounded-md px-3">
            <Mail className="h-4 w-4 text-primary shrink-0" />
            <Input placeholder="E-mail" value={form.email} onChange={(e) => set("email", e.target.value)} className="border-0 bg-transparent px-0 focus-visible:ring-0" />
          </div>
        </div>

        <div className="space-y-3 pt-2">
          <div className="flex items-start gap-2">
            <Checkbox id="aceite" checked={aceito} onCheckedChange={(v) => { setAceito(!!v); if (v) track("criar_anuncio_aceitou_termos", { type: "funnel" }); }} className="mt-0.5" />
            <label htmlFor="aceite" className="text-sm text-muted-foreground leading-snug cursor-pointer">
              Li e aceito os{" "}
              <Link to="/termos" target="_blank" className="text-primary underline font-medium">termos de uso</Link>{" "}
              e a{" "}
              <Link to="/privacidade" target="_blank" className="text-primary underline font-medium">política de privacidade</Link>.
            </label>
          </div>
          <TurnstileWidget onToken={setTurnstileToken} />
        </div>

        <Button className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-semibold h-12 text-base"
          onClick={handlePublish}
          disabled={!aceito || !turnstileToken || loading || uploading || (!!plan && !plan.is_admin && plan.listings_limit !== -1 && plan.listings_left <= 0)}>
          {loading ? "Publicando..." : (
            (plan?.is_admin || plan?.listings_limit === -1) ? (
              <span className="flex flex-col items-center leading-tight">
                <span>Publicar com plano {plan?.is_admin ? "Admin" : plan?.plan_name}</span>
                <span className="text-[11px] font-normal opacity-90">Anúncios ilimitados</span>
              </span>
            ) : (plan && plan.listings_left <= 0) ? (
              <span className="flex flex-col items-center leading-tight">
                <span>Limite atingido</span>
                <span className="text-[11px] font-normal opacity-90">Faça upgrade em Planos</span>
              </span>
            ) : (
              <span className="flex flex-col items-center leading-tight">
                <span>Publicar gratuitamente</span>
                {plan && <span className="text-[11px] font-normal opacity-90">{plan.listings_left} de {plan.listings_limit} anúncios restantes</span>}
              </span>
            )
          )}
        </Button>
      </div>
    </div>
  );
}
